import React, { useEffect, useMemo, useState } from "react";
import { Animated, StyleSheet, View } from "react-native";
import { formatMoney, getDisplayItems } from "@rista/core/customerDisplay";
import {
  DEFAULT_AMOUNT_DECIMALS,
  DEFAULT_AMOUNT_LOCALE,
} from "@rista/core/utils/currency/format";

import { useCustomerDisplayAnimation, useDisplayMedia, useOrderSuccessHold } from "./hooks";
import { MediaSection, MobileNumberEntryScreen, OrderSection, PaymentSection } from "./components";


export const CustomerDisplay = ({
  sale,
  /**
   * 🔴 **The three halves of the merchant's money format, and all three default HERE.** This is a
   * WIRE boundary, not a call site: web's payload crosses a window, so a display that mounted
   * before the first push — or one talking to a shell older than 2026-09-19 — arrives with none
   * of them. Mobile always supplies all three; it reads the same store.
   *
   * They default together on purpose. Only `decimals` is rescued further down
   * (`amountDecimalsFor` inside `formatAmount` turns an absent value into two), so it alone
   * would survive being left bare — but a reader cannot see that from here, and the other two
   * fail loudly and quietly respectively: an absent symbol prints `undefined1,234.50`, an absent
   * locale falls to the ungrouped branch and drops every separator with nothing to notice it.
   *
   * ⚠️ These cover ABSENT only. A value that arrives present but unusable is still
   * `amountDecimalsFor`'s job — the two layers do different work and both stay.
   */
  currency = "",
  decimals = DEFAULT_AMOUNT_DECIMALS,
  grouping = DEFAULT_AMOUNT_LOCALE,
  campaignConfig,
  staticMediaBaseUrl,
  logoUrl,
  itemDisplayInfo,
  paymentDetails,
  isPaymentInProcess = false,
  canCustomerEnterNumber = false,
  fetchCustomerNumber,
  saleClosedAt,
}: any) => {

  /**
   * ONE formatter, built here and passed down — rather than a symbol and a precision drilled
   * through three levels and recombined at five leaves. The sections print money without knowing
   * which currency or how many digits, the same shape the kiosk uses (`useKioskMoney`).
   */
  const money = useMemo(
    () => (value: unknown) => formatMoney(value, currency, { decimals, grouping }),
    [currency, decimals, grouping],
  );

  const items = getDisplayItems(sale);
  const isOrderSuccess = useOrderSuccessHold(saleClosedAt);
  // The cart is already empty by the time a sale closes, and an empty cart is the idle screen.
  // Counting the success hold as "has items" is what keeps the display in its ORDER state —
  // panel open, order media playing — long enough for the customer to read the confirmation.
  const hasItems = items.length > 0 || isOrderSuccess;
  const [mobileNumber, setMobileNumber] = useState("");
  const [isMobileNumberMasked, setIsMobileNumberMasked] = useState(true);

  const {
    idleMedia,
    orderMedia,
    idleDwellSeconds,
    orderDwellSeconds,
    mediaSplitTarget,
    orderSplitTarget,
    hasOrderMedia,
    orientation,
  } = useDisplayMedia(campaignConfig, staticMediaBaseUrl);

  const isPortrait = orientation === "portrait";

  const {
    animatedMediaWidth,
    animatedMediaHeight,
    animatedCartWidth,
    animatedCartHeight,
    animatedCartTranslateX,
    animatedCartTranslateY,
    animatedCartOpacity,
  } = useCustomerDisplayAnimation({
    hasItems,
    hasOrderMedia,
    mediaSplitTarget,
    orderSplitTarget,
    orientation,
  });

  const displayMedia = hasItems ? orderMedia : idleMedia;
  const dwellSeconds = hasItems ? orderDwellSeconds : idleDwellSeconds;

  // PaymentSection owns the success screen too, so the hold has to open this pane: the payment
  // popup is closed (isPaymentInProcess false) before the display hears the sale closed.
  const showPayment = isPaymentInProcess || isOrderSuccess;

  // The display stays mounted across orders, so the keypad state has to be
  // wiped every time the entry screen opens or closes — otherwise the number
  // the previous customer typed is still sitting there for the next sale.
  // Clearing on close (not only on open) also avoids showing the old number
  // for a frame before the reset lands.
  useEffect(() => {
    setMobileNumber("");
    setIsMobileNumberMasked(true);
  }, [canCustomerEnterNumber]);

  const handleContinueClick = () => {
    fetchCustomerNumber?.(mobileNumber);
  };
  if (canCustomerEnterNumber) {
    return (
      <MobileNumberEntryScreen
        mobileNumber={mobileNumber}
        onChangeMobileNumber={setMobileNumber}
        isMasked={isMobileNumberMasked}
        onToggleMasked={() =>
          setIsMobileNumberMasked((previous) => !previous)
        }
        logoUrl={logoUrl}
        onContinue={handleContinueClick}
      />
    );
  }

  return (
    <View style={styles.screen}>
      <View style={[styles.splitLayout, isPortrait && styles.splitLayoutPortrait]}>
        <Animated.View
          style={[
            styles.mediaContainer,
            {
              width: animatedMediaWidth,
              height: animatedMediaHeight,
            },
          ]}
        >
          <MediaSection media={displayMedia} dwellSeconds={dwellSeconds} />
        </Animated.View>

        <Animated.View
          style={[
            styles.cartContainer,
            {
              width: animatedCartWidth,
              height: animatedCartHeight,
              opacity: animatedCartOpacity,
              transform: [
                {
                  translateX: animatedCartTranslateX,
                },
                {
                  translateY: animatedCartTranslateY,
                },
              ],
            },
          ]}
        >
          {showPayment ? (
            <PaymentSection
              sale={sale}
              money={money}
              paymentDetails={paymentDetails}
              isPaymentInProcess={isPaymentInProcess}
              isOrderSuccess={isOrderSuccess}
            />
          ) : (
            <OrderSection
              sale={sale}
              items={items}
              money={money}
              itemDisplayInfo={itemDisplayInfo}
            />
          )}
        </Animated.View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    width: "100%",
    height: "100%",
    backgroundColor: "#FFFFFF",
  },

  splitLayout: {
    flex: 1,
    width: "100%",
    height: "100%",
    flexDirection: "row",
    overflow: "hidden",
  },

  splitLayoutPortrait: {
    flexDirection: "column",
  },

  mediaContainer: {
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden",
  },

  cartContainer: {
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
  },
});

export default CustomerDisplay;